<template>
	<div class="paged-list-filter">
		<o-section
			title="Filtrer par"
			class="filter-section__subsection">
			<div :class="{ 'form row row--grid' : aside }">
				<div
					v-if="!aside"
					class="col-sm-6">
					<o-input
						v-model="setup.filter.research"
						label="Chercher"
						class="filter-section__subsection-input"
						suffix-font-icon="search-1"/>
				</div>

				<div
					v-if="setup.groupOptions.length"
					class="col-sm-6">
					<o-select
						v-model="setup.filter.talentPoolIds"
						multiple
						label="Vivier"
						class="filter-section__subsection-input"
						display-key="label"
						value-key="id"
						track-key="id"
						:disabled="setup.disabled.includes('talentPoolIds')"
						:options="setup.filteredGroupOptions"/>
				</div>

				<div
					v-if="setup.skillOptions.length"
					class="col-sm-6">
					<o-select
						v-model="setup.filter.professionalSkillIds"
						multiple
						searchable
						display-key="label"
						track-key="id"
						value-key="id"
						label="Qualifications"
						class="filter-section__subsection-input"
						:options="setup.skillOptions"/>
				</div>

				<div
					v-if="setup.inModal && setup.skillOptions.length"
					class="col-sm-6">
					<o-select
						v-model="setup.filter.orderProfessionalSkillsProfessionalSkillIds"
						multiple
						searchable
						display-key="label"
						track-key="id"
						value-key="id"
						label="Qualifications secondaires"
						class="filter-section__subsection-input"
						:options="setup.skillOptions"/>
				</div>

				<div class="col-sm-6">
					<shared-select-agency-qualifications
						v-model="setup.filter.userAgencyQualifications"
						:agency-company-id="setup.filter.agencyCompanyId"/>
				</div>

				<div
					v-if="!setup.isLoading"
					class="col-sm-6">
					<o-select
						v-model="setup.filter.languageIds"
						multiple
						display-key="name"
						track-key="id"
						value-key="id"
						label="Langues"
						:options="setup.languageOptions"/>
				</div>

				<div class="col-sm-6">
					<o-input
						v-model="setup.filter.numberOfContractsGte"
						mask="integer"
						:max-value="1000"
						label="Nombre de missions effectuées"/>
				</div>

				<div class="col-sm-6">
					<shared-select-user-company
						v-model="setup.filter.agencyCompanyId"
						:disabled="setup.disabled.includes('agencyCompanyId')"/>
				</div>

				<div
					v-if="!setup.candidateService.inOrder()"
					class="col-sm-6">
					<o-select
						v-model="setup.filter.status"
						label="État"
						display-key="label"
						track-key="value"
						value-key="value"
						:options="setup.statusOptions"
						clearable/>
				</div>
			</div>
		</o-section>

		<o-section
			title="Mobilité"
			class="filter-section__subsection">
			<div :class="{ 'form row row--grid' : aside }">
				<div class="col-sm-6">
					<o-select
						v-model="setup.filter.mobility"
						clearable
						label="Mobilité - Rayon"
						:options="setup.mobilityOptions"
						track-key="value"
						display-key="label"
						value-key="value"/>
				</div>
				<div class="col-sm-6 flex ai-c">
					<o-checkbox
						v-model="setup.filter.hasPersonalVehicle"
						label="Possède un véhicule personnel"/>
				</div>
			</div>
		</o-section>

		<o-section
			v-if="(!aside && setup.candidateService.inOrder()) || !setup.candidateService.inOrder()"
			title="Disponibilités"
			foldable
			class="filter-section__subsection">
			<div :class="{ 'form row row--grid': aside }">
				<div class="col-xs-12 col-sm-6">
					<o-datepicker
						v-model:range="setup.filter.dateRange"
						clearable
						type="range"
						label="Sélectionnez une période"/>
				</div>
			</div>
		</o-section>

		<o-section
			class="filter-section__subsection"
			title="Performances">
			<div
				v-for="i in 5"
				:key="i"
				class="filter-section__radios"
				:class="{ 'filter-section__radios--selected': setup.filter.rating === i }"
				@click="setup.toggleRateFilter(i)">
				<o-radio
					v-model="setup.filter.rating"
					:input-value="i"
					@click.stop/>
				<o-rate
					v-model="setup.notes[i]"
					disabled
					font-icon="icon-interface-favorite-star-bold"
					color="warning"/>
				<div>
					{{ i }} <span
						v-if="i < 5"
						style="color: grey">  ou plus</span>
				</div>
			</div>
		</o-section>

		<o-section
			align="stretch"
			class="filter-section__actions">
			<o-button
				v-if="!setup.inModal"
				color="danger"
				outline
				@click="setup.cancelFilter()">
				Annuler
			</o-button>
			<o-button
				outline
				@click="setup.resetFilter()">
				Réinitialiser
			</o-button>
			<o-button
				color="info"
				@click="setup.applyFilterAsync(_modal)">
				Appliquer
			</o-button>
		</o-section>
	</div>
</template>

<script setup lang="ts">
import TempWorkerPagedListFilterSetupService from '@/setup/temp-worker/TempWorkerPagedListFilterSetupService';
import { inject } from 'vue';
const props = defineProps(TempWorkerPagedListFilterSetupService.props);
const _modal = inject<OrionModal>('_modal');
const _aside = inject<OrionAside>('_aside');
const setup = new TempWorkerPagedListFilterSetupService(props, _aside);
</script>

<style lang="less" scoped>
@import '@/styles/variables';

.filter-section {
	&__radios {
		display: flex;
		align-items: center;
		gap: 1.25rem;
		background: var(--grey-lighter);
		padding: 0 0.6rem;
		border-radius: 0.3rem;
		margin-bottom: 0.3rem;
		cursor: pointer;

    &:first-child {
      margin-top: 0.5rem;
    }

		&--selected {
			background: rgba(var(--rgb-info), 0.10);
		}

		&:hover {
			opacity: 0.7;
		}
	}

	&__subsection{
		h2 {
			font-size: 1rem;
			font-weight: 600;
			color: var(--grey-darker);
		}
		.orion-section__header {
			height: 2.5rem;
		}
	}

  #left-aside & {
    &__actions {
      transition: background-color 0.3s;
      position: sticky;
      bottom: -2.5rem;
      background-color: var(--grey-lighter);
      z-index: 1;
      margin: 1rem -1rem -1rem;
      padding: 1rem;
    }
  }

  #left-aside .paged-list-filter:hover & {
    &__actions {
      background: var(--filter-hover-bg-color);
    }
  }

	.row {
		.orion-input {
			margin: unset;
		}
	}
}
</style>
